<template>
  <InputText
    v-if="loading"
    loading
  />
  <input
    v-else
    :value="displayValue"
    v-bind="timeInputProps"
    type="time"
    :disabled="disabled"
    :min="minTimeComputed"
    :max="maxTimeComputed"
    :step="showSeconds ? 1 : 60"
    class="p-inputtext p-component p-filled"
    :class="{
      'p-invalid': invalidComputed,
    }"
    @change="emit('update:modelValue', parseMilliseconds(($event.target as HTMLInputElement).value))"
  >
</template>

<script setup lang="ts">
import {
  InputHTMLAttributes,
  computed,
} from 'vue';

import {
  DAY_MS,
  HOURS_MS,
  MINUTES_MS,
  SECONDS_MS,
} from '../../constants/time.ts';
import {
  formatMilliseconds,
} from '../../utils/date-formats.ts';
import InputText from '../input-text/input-text.vue';

type TProperties = {
  /* Time from start of the day in milliseconds */
  modelValue?: number,
  min?: number,
  max?: number,
  invalid?: boolean,
  showSeconds?: boolean,
  disabled?: boolean,
  timeInputProps?: InputHTMLAttributes,
  loading?: boolean,
};
const props = defineProps<TProperties>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void,
}>();

function validateMillisecondsProperty(which: Extract<keyof TProperties, 'modelValue' | 'min' | 'max'>) {
  const propVal = props[which];
  if (propVal !== undefined && propVal > DAY_MS) {
    throw new Error(`Property ${which} cannot exceed ${props[which]} (24h).`);
  }
}

const displayValue = computed(() => {
  if (props.modelValue === undefined) {
    return undefined;
  }
  validateMillisecondsProperty('modelValue');
  return formatMilliseconds(props.modelValue, props.showSeconds);
});

const minTimeComputed = computed(() => {
  if (!props.min) {
    return undefined;
  }
  validateMillisecondsProperty('min');
  return formatMilliseconds(props.min, props.showSeconds);
});

const invalidComputed = computed(() => props.invalid || (props.max && props.min && (props.min > props.max)));

const maxTimeComputed = computed(() => {
  if (!props.max) {
    return undefined;
  }
  validateMillisecondsProperty('min');

  if (props.min && props.min > props.max) {
    throw new Error('min Time cant not be greater than max Time');
  }

  return formatMilliseconds(props.max, props.showSeconds);
});

function parseMilliseconds(mills: string): number {
  const [
    hour,
    minute,
    second,
  ] = mills.split(':').map((itm) => (itm !== undefined ? parseInt(itm, 10) : 0));

  return hour * HOURS_MS + minute * MINUTES_MS + (second || 0) * SECONDS_MS;
}
</script>

<style scoped lang="scss">
.input_disabled {
  color: var(--color-233);
}
</style>
